import { Box, Text } from "@chakra-ui/react";
import { Attribute, mergeAttributes, Node } from "@tiptap/core";
import { NodeViewWrapper, ReactNodeViewRenderer } from "@tiptap/react";
import { TimeFormatType } from "interfaces/models/inspectionItemResult";
import {
  DEFAULT_PIN_COMPONENT_STYLE,
  PinComponentStyle,
} from "interfaces/models/pinComponent";

import { useRef, useState } from "react";
import { CustomNodeComponentProps, NodeType } from "../type";
import { dispatchUpdateContentEvent, handleCommandForNode } from "../utils";
import TimePicker, { TimePickerRef } from "components/TimePicker";

export interface TimeNodeAttrs {
  time: string | null;
  formatType: TimeFormatType;
  style?: PinComponentStyle;
}

export const TimeNode = Node.create({
  name: NodeType.TIME,
  group: "block",
  selectable: false,

  addAttributes() {
    const attrs: {
      [key in keyof TimeNodeAttrs]: Attribute;
    } = {
      time: { default: undefined },
      style: { default: DEFAULT_PIN_COMPONENT_STYLE },
      formatType: { default: TimeFormatType["HH:mm"] },
    };

    return attrs;
  },
  parseHTML() {
    return [
      {
        tag: "datetime",
      },
    ];
  },
  renderHTML({ HTMLAttributes }) {
    return ["datetime", mergeAttributes(HTMLAttributes)];
  },

  addNodeView() {
    return ReactNodeViewRenderer(Component);
  },
});

const Component = (props: CustomNodeComponentProps<TimeNodeAttrs>) => {
  const { formatType, time, style } = props.node.attrs;
  const editorAttributes = props.editor.options.editorProps?.attributes as any;
  const isPreview = editorAttributes?.isPreview === "true";
  const [isValidInputTime, setIsValidInputTime] = useState(true);
  const [isShowCalendar, setIsShowCalendar] = useState<boolean>(false);

  const timePickerRef = useRef<TimePickerRef | null>(null);

  const onChange = async (updateTime: string) => {
    props.updateAttributes({ time: updateTime, formatType });
    // update pin component node
    const pos = props.getPos() - 1;
    const node = props.editor.state.doc.nodeAt(pos);
    if (!node || isPreview) {
      return;
    }

    handleCommandForNode({
      isFocusEditor: false,
      editor: props.editor,
      command: ({ tr }) => {
        tr.setNodeMarkup(pos, undefined, {
          ...node.attrs,
          isUpdated: true,
          updatedAt: new Date(),
        });

        return true;
      },
    });

    dispatchUpdateContentEvent(props.editor);

    return;
  };

  return (
    <NodeViewWrapper
      style={{
        width: "100%",
        height: "100%",
        display: "flex",
        alignItems: style?.alignItems,
        cursor: "default",
        pointerEvents: "auto",
        position: "relative",
      }}
      className={`wrapper-node-date ${isValidInputTime ? "" : "has-error"} ${
        isShowCalendar ? "wrapper-node-time-focusing" : ""
      }`}
    >
      <Box
        style={{
          width: "100%",
          height: "100%",
          display: "flex",
          alignItems: style?.alignItems,
        }}
        onClick={() => {
          if (isPreview) {
            return;
          }
          timePickerRef?.current?.focusInput();
        }}
      >
        {!isValidInputTime && (
          <Text fontSize="1.4rem" className="tooltip-text">
            時間形式が無効です
          </Text>
        )}
        <Box width="100%" height="fit-content">
          <TimePicker
            ref={timePickerRef}
            isReadOnly={isPreview}
            value={time}
            showIcon={false}
            isShowCalendar={isShowCalendar}
            setIsShowCalendar={setIsShowCalendar}
            isShowTooltip={false}
            formatType={formatType}
            onChangeStatusValidTime={setIsValidInputTime}
            inputStyle={{
              minHeight: "1em",
              textAlign: style?.textAlign,
              fontSize: `${style?.fontSize}px`,
              height: "initial",
              width: "100%",
              paddingLeft: 0,
              backgroundColor: "transparent",
              border: "none",
              boxShadow: "none",
              ...(isPreview ? { paddingRight: 0 } : {}),
            }}
            onChange={onChange as any}
          />
        </Box>
      </Box>
    </NodeViewWrapper>
  );
};
